/**
 * Session Page Component
 *
 * Routes to the appropriate interaction type component based on
 * interactionType from window.__PAYMENT_SESSION__ (injected by Lambda BFF).
 *
 * URL format: https://agent.btpay.com?id=<agentSessionId>
 */

import { extractSessionIdFromUrl } from '@utils/urlParser';
import { APP_CONFIG } from '@utils/constants';
import { logger } from '@utils/logger';
import { useSession } from '@hooks/useSession';
import ChatPaymentPage from './ChatPaymentPage';
import VoicePaymentPage from './VoicePaymentPage';
import VoiceManualPaymentPage from './VoiceManualPaymentPage';
import SessionNotFound from './SessionNotFound';
import styles from './SessionPage.module.css';
import type { Session } from '@/types/session.types';

interface SessionPageProps {
  /**
   * Pre-validated payment session from window.__PAYMENT_SESSION__ (read in main.tsx).
   * Contains interactionType for routing and credentials for useAgentApi.
   */
  initialSession?: PaymentSession;
}

function renderByInteractionType(
  session: Session,
  initialSession?: PaymentSession
) {
  switch (session.interactionType) {
    case 'CHAT':
      return (
        <ChatPaymentPage session={session} initialSession={initialSession} />
      );
    case 'VOICE':
      return (
        <VoicePaymentPage session={session} initialSession={initialSession} />
      );
    case 'VOICE_MANUAL':
      return (
        <VoiceManualPaymentPage
          session={session}
          initialSession={initialSession}
        />
      );
    default:
      return (
        <div style={{ textAlign: 'center' }}>
          <h1>Unknown Interaction Type</h1>
          <p>
            Interaction type &quot;{session.interactionType}&quot; is not
            supported.
          </p>
        </div>
      );
  }
}

export default function SessionPage({ initialSession }: SessionPageProps) {
  const sessionId = extractSessionIdFromUrl();

  logger.debug('Session page initialized', {
    useMockData: APP_CONFIG.USE_MOCK_DATA,
    sessionId,
  });

  // Mock mode: delegate to hook-based component that uses useSession mock data
  if (APP_CONFIG.USE_MOCK_DATA) {
    return (
      <MockSessionPage sessionId={sessionId} initialSession={initialSession} />
    );
  }

  // Production mode: route directly from initialSession — no backend call needed.
  // interactionType comes from window.__PAYMENT_SESSION__ injected by the Lambda BFF.
  if (!initialSession) {
    return <SessionNotFound sessionId={sessionId || ''} />;
  }

  const session: Session = {
    sessionId: initialSession.sessionId,
    interactionType: initialSession.interactionType,
    customerId: initialSession.paymentId,
    amount: initialSession.amount,
    currency: initialSession.currency,
    paymentId: initialSession.paymentId,
    expiresAt: initialSession.expiresAt,
  };

  logger.info('Routing session by interactionType', {
    interactionType: initialSession.interactionType,
    sessionId,
  });

  return (
    <div className={styles.pageContainer}>
      <div className={styles.componentWrapper}>
        {renderByInteractionType(session, initialSession)}
      </div>
    </div>
  );
}

/**
 * Mock-only wrapper — uses useSession hook which generates mock session data.
 * Only rendered when VITE_USE_MOCK_DATA=true.
 */
function MockSessionPage({
  sessionId,
  initialSession,
}: {
  sessionId: string | null;
  initialSession?: PaymentSession;
}) {
  const { session, loading, error, retry } = useSession(sessionId || '', {
    useMockData: true,
  });

  if (loading) {
    return (
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem',
          }}
        />
        <p>Loading session...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    if (
      error.message.includes('404') ||
      error.message.toLowerCase().includes('not found')
    ) {
      return <SessionNotFound sessionId={sessionId || ''} />;
    }
    return (
      <div
        style={{
          textAlign: 'center',
          maxWidth: '600px',
          margin: '2rem auto',
        }}
      >
        <h1>Error</h1>
        <p>{error.message}</p>
        <button
          onClick={retry}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            fontSize: '1rem',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!session) {
    return <SessionNotFound sessionId={sessionId || ''} />;
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.componentWrapper}>
        {renderByInteractionType(session, initialSession)}
      </div>
    </div>
  );
}
